import { CONTAINER_STORAGE_FIELD_KEYS } from '../../capabilities/container-storage';
import { ENV_VARS_FIELD_KEYS } from '../../capabilities/env-vars/fieldKeys';
import { MAAS_COMMAND_ARGS_NORMAL_FIELD_KEYS } from '../../capabilities/maas-command-args-normal/fieldKeys';
import { MAAS_NODE_GROUPS_FIELD_KEYS } from '../../capabilities/maas-node-groups/fieldKeys';
import { MAAS_SYSTEM_CONFIG_FIELD_KEYS } from '../../capabilities/maas-system-config/fieldKeys';
import { TC_CAPABILITIES } from './capabilities';
import type { TcFieldKey } from './fields';

function binding(key: (typeof TC_CAPABILITIES)[number]['key']) {
  const result = TC_CAPABILITIES.find((item) => item.key === key);
  if (!result) throw new Error(`TrainingCluster capability binding not found: ${key}`);
  return result;
}

/**
 * 训练集群 SKU、下单共用模块定义。
 * modules.fields 必须使用 capability 逻辑 key（maas-* 历史上以 `mi.*` 为前缀，属共享命名空间，非 ModelInstance 私有）。
 */
export const TC_MODULES = [
  {
    key: 'systemConfig',
    title: '训练镜像',
    capabilities: [binding('maas-system-config')],
    fields: [
      'trainingCluster.runtime.type',
      MAAS_SYSTEM_CONFIG_FIELD_KEYS.framework,
      MAAS_SYSTEM_CONFIG_FIELD_KEYS.inferenceSource,
      MAAS_SYSTEM_CONFIG_FIELD_KEYS.inferenceSystem,
      MAAS_SYSTEM_CONFIG_FIELD_KEYS.inferenceFrameworkImage,
    ],
    presetCheckFields: ['runtime.type', '_inferenceSystem', 'inferenceFrameworkImage'],
    resetFieldPaths: ['runtime', 'framework', 'maasInferenceSource', '_inferenceSystem', 'inferenceFrameworkImage'],
  },
  {
    key: 'nodeGroups',
    title: '节点配置',
    capabilities: [binding('maas-node-groups')],
    fields: [
      MAAS_NODE_GROUPS_FIELD_KEYS.runMode,
      MAAS_NODE_GROUPS_FIELD_KEYS.masterNodes,
      MAAS_NODE_GROUPS_FIELD_KEYS.workerNodes,
      MAAS_NODE_GROUPS_FIELD_KEYS.masterSystemDisk,
      MAAS_NODE_GROUPS_FIELD_KEYS.workerSystemDisk,
      MAAS_NODE_GROUPS_FIELD_KEYS.replicas,
    ],
    presetCheckFields: ['masterNodes', 'workerNodes'],
    resetFieldPaths: [
      'runMode',
      'mode',
      'masterNodes',
      'workerNodes',
      'master',
      'worker',
      'master.systemDiskSize',
      'worker.systemDiskSize',
      'masterSystemDiskPricing',
      'workerSystemDiskPricing',
      'replicas',
    ],
  },
  {
    key: 'trainingNic',
    title: '网卡',
    capabilities: [],
    fields: ['trainingCluster.network.nics', 'trainingCluster.network.nicPricing'],
    presetCheckFields: ['network.nics'],
    resetFieldPaths: ['network.nics', 'network.nicPricing'],
  },
  {
    key: 'storage',
    title: '共享存储卷',
    capabilities: [binding('container-storage')],
    fields: [CONTAINER_STORAGE_FIELD_KEYS.list],
    presetCheckFields: ['trainingSharedVolumes'],
    resetFieldPaths: ['trainingSharedVolumes'],
  },
  {
    key: 'envVars',
    title: '环境变量',
    capabilities: [binding('env-vars')],
    fields: [ENV_VARS_FIELD_KEYS.list],
    presetCheckFields: ['commonEnv'],
    resetFieldPaths: ['commonEnv'],
  },
  {
    key: 'initCommand',
    title: '初始化命令和参数',
    capabilities: [binding('maas-command-args-normal')],
    fields: [
      MAAS_COMMAND_ARGS_NORMAL_FIELD_KEYS.shellType,
      MAAS_COMMAND_ARGS_NORMAL_FIELD_KEYS.commands,
      MAAS_COMMAND_ARGS_NORMAL_FIELD_KEYS.args,
    ],
    presetCheckFields: ['initCommand'],
    resetFieldPaths: ['initCommand'],
  },
] as const satisfies readonly {
  key: string;
  title: string;
  capabilities: readonly (typeof TC_CAPABILITIES)[number][];
  fields: readonly TcFieldKey[];
  presetCheckFields?: readonly string[];
  resetFieldPaths?: readonly string[];
}[];

/** 训练集群模块索引。 */
export const TC_MODULE_MAP = Object.fromEntries(TC_MODULES.map((item) => [item.key, item]));
